import React, { useCallback, useEffect, useRef } from 'react';

import TopNavItem from '@components/Nav/TopNav/NavItem';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import { useSectionRegistryState } from './SectionRegistryContext';
import { FOR_YOU_NAV_ITEMS } from './constants';

const SCROLL_DELAY_MS = 100;

const ForYouNav = () => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Nav,
  });
  const registeredSections = useSectionRegistryState();
  const hasScrolledRef = useRef(false);

  const visibleNavItems = FOR_YOU_NAV_ITEMS.filter((item) => registeredSections.has(item.id));

  const scrollToSection = useCallback((sectionId: string) => {
    const target = document.getElementById(sectionId);
    if (!target) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }, []);

  const handleNavClick = useCallback(
    (sectionId: string) => {
      scrollToSection(sectionId);
      window.history.pushState(null, '', `#${sectionId}`);
      trackEvent(click({ clickTarget: ClickTarget.NavLink, clickText: sectionId }));
    },
    [scrollToSection, trackEvent]
  );

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (!hasScrolledRef.current && hash && registeredSections.has(hash)) {
      const timeout = setTimeout(() => {
        scrollToSection(hash);
        hasScrolledRef.current = true;
      }, SCROLL_DELAY_MS);
      return () => clearTimeout(timeout);
    }
  }, [registeredSections, scrollToSection]);

  const visibleNavItemsLength = visibleNavItems.length;

  if (visibleNavItemsLength === 0) return null;

  return (
    <nav
      aria-label="Page sections"
      className="hidden border-0 border-b border-t border-solid border-grayscale-beige py-32 sm:block [&+_article]:mt-0 [&_+_article]:border-t-0 [&_+_article]:pt-48 [&_+_article]:sm:pt-64"
    >
      <ul className="m-0 flex flex-1 list-none flex-row flex-wrap items-center justify-center p-0 sm:gap-24">
        {visibleNavItems.map((item, index) => (
          <TopNavItem
            key={item.id}
            as="button"
            type="button"
            className="appearance-none before:hidden sm:before:block"
            showLeftDivider={index > 0}
            onClick={() => handleNavClick(item.id)}
          >
            {item.label}
          </TopNavItem>
        ))}
      </ul>
    </nav>
  );
};

export default ForYouNav;
